import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  TextField,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormLabel,
  Paper,
  Divider,
  Tooltip,
  Alert
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  Numbers as NumbersIcon,
  List as ListIcon,
  ViewModule as CardIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TEMPLATE_PRESETS = {
  numbered_list: {
    name: "Numbered List",
    icon: "📝",
    template: "{#}. {field1}",
    description: "Simple numbered list",
    sample: "1. Dr. John Doe\n2. Dr. Jane Smith"
  },
  detailed_card: {
    name: "Detailed Cards", 
    icon: "🃏",
    template: "**{field1}**\n🏥 {field2}\n📋 {field3}\n---",
    description: "Rich card format with icons",
    sample: "**Dr. John Doe**\n🏥 General Hospital\n📋 Cardiology\n---"
  },
  simple_options: {
    name: "Choice Options",
    icon: "✅", 
    template: "{#}. {field1} - {field2}",
    description: "For user selection",
    sample: "1. Dr. John Doe - Cardiology\n2. Dr. Jane Smith - Neurology"
  },
  custom: {
    name: "Custom Format",
    icon: "🎨",
    template: "",
    description: "Build your own format",
    sample: "Create your own layout..."
  }
};

const SortableItem = ({ field, onRemove, onUpdateField, formatOptions }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: field.path });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        p: 2,
        mb: 1,
        backgroundColor: '#f0f0f0',
        borderRadius: 1,
        border: '1px solid #e0e0e0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: formatOptions.fieldsOnSeparateLines ? 1.5 : 0 }}>
        <Box {...attributes} {...listeners} sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}>
          <DragIcon sx={{ color: '#666' }} />
        </Box>
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
          {field.label}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onRemove(field.path)}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      
      {formatOptions.fieldsOnSeparateLines && (
        <Box sx={{ ml: 4 }}>
          <TextField
            size="small"
            label="Field Label (optional)"
            value={field.customLabel || ''}
            onChange={(e) => onUpdateField(field.path, { ...field, customLabel: e.target.value })}
            placeholder={`e.g., "Doctor Name:", "Hospital:", etc.`}
            fullWidth
            helperText="Leave empty to show just the value"
            sx={{ mb: 1 }}
          />
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            Field path: {field.path}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const ListBuilderModal = ({ 
  open, 
  onClose, 
  availableArrays = [], 
  onInsertTemplate,
  availableVariables = [],
  availableApiEndpoints = [] // Add API endpoints to extract field structure
}) => {
  const [selectedArray, setSelectedArray] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [formatOptions, setFormatOptions] = useState({
    numbered: true,
    separatorType: 'line_break',
    fieldSeparator: ' - ',
    showBullets: false,
    customSeparator: '',
    fieldsOnSeparateLines: false,
    showFieldLabels: true
  });
  const [selectedPreset, setSelectedPreset] = useState('numbered_list');
  const [arrayStructure, setArrayStructure] = useState(null);
  const [previewText, setPreviewText] = useState('');

  // Get array structure when array is selected
  useEffect(() => {
    if (selectedArray && availableArrays.length > 0) {
      const arrayVar = availableArrays.find(arr => arr.name === selectedArray);
      if (arrayVar && arrayVar.sampleData) {
        analyzeArrayStructure(arrayVar.sampleData);
      }
    }
  }, [selectedArray, availableArrays]);

  // Update preview when configuration changes
  useEffect(() => {
    if (selectedArray && selectedFields.length > 0) {
      generatePreview();
    }
  }, [selectedArray, selectedFields, formatOptions, selectedPreset]);

  const analyzeArrayStructure = (sampleData) => {
    console.log('🔍 [ListBuilderModal] analyzeArrayStructure called with sampleData:', sampleData);
    
    let combinedFields = [];
    let combinedSampleData = [];
    
    // Extract from provided sample data
    if (sampleData && Array.isArray(sampleData) && sampleData.length > 0) {
      console.log('📋 [ListBuilderModal] Extracting fields from variable sample data');
      const fields = extractFieldsFromData(sampleData);
      combinedFields = [...combinedFields, ...fields];
      combinedSampleData = [...sampleData.slice(0, 2)];
    }
    
    // ENHANCEMENT: Also extract from API Endpoint Response Example
    const apiEndpointFields = extractFieldsFromApiEndpoint();
    if (apiEndpointFields.length > 0) {
      console.log('🌐 [ListBuilderModal] Extracting fields from API endpoint response example');
      console.log('🌐 [ListBuilderModal] API endpoint fields found:', apiEndpointFields.length);
      
      // Merge API endpoint fields with existing fields (API endpoint takes priority)
      const mergedFields = [...apiEndpointFields];
      
      // Add variable sample fields that don't conflict
      combinedFields.forEach(varField => {
        const existsInApi = apiEndpointFields.some(apiField => apiField.path === varField.path);
        if (!existsInApi) {
          mergedFields.push(varField);
        }
      });
      
      combinedFields = mergedFields;
      
      // Use API endpoint sample data if available
      if (apiEndpointFields.sampleData && Array.isArray(apiEndpointFields.sampleData)) {
        combinedSampleData = apiEndpointFields.sampleData.slice(0, 2);
      }
    }
    
    if (combinedFields.length === 0) {
      console.log('⚠️ [ListBuilderModal] No fields found from either sample data or API endpoint');
      setArrayStructure(null);
      return;
    }
    
    console.log('✅ [ListBuilderModal] Final combined fields:', combinedFields.length, combinedFields);
    setArrayStructure({ 
      fields: combinedFields, 
      sampleData: combinedSampleData,
      source: apiEndpointFields.length > 0 ? 'api_endpoint' : 'variable_sample'
    });
  };

  const extractFieldsFromData = (sampleData) => {
    const fields = [];
    const firstItem = sampleData[0];
    
    const extractFields = (obj, path = '', level = 0) => {
      if (level > 3) return; // Prevent deep nesting
      
      Object.keys(obj).forEach(key => {
        const value = obj[key];
        const currentPath = path ? `${path}.${key}` : key;
        
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          extractFields(value, currentPath, level + 1);
        } else if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          fields.push({
            path: currentPath,
            label: formatFieldLabel(currentPath),
            type: typeof value,
            sampleValue: value
          });
        }
      });
    };
    
    extractFields(firstItem);
    return fields;
  };

  const extractFieldsFromApiEndpoint = () => {
    if (!selectedArray || !availableApiEndpoints || availableApiEndpoints.length === 0) {
      return [];
    }
    
    // Find which API endpoint might have generated this array variable
    // Check if this array variable comes from a step that uses an API endpoint
    const arrayVar = availableArrays.find(arr => arr.name === selectedArray);
    if (!arrayVar || !arrayVar.sourceApiEndpoint) {
      console.log('🔍 [ListBuilderModal] No source API endpoint found for array variable:', selectedArray);
      return [];
    }
    
    // Find the API endpoint
    const apiEndpoint = availableApiEndpoints.find(ep => ep._id === arrayVar.sourceApiEndpoint);
    if (!apiEndpoint || !apiEndpoint.responseExample) {
      console.log('🔍 [ListBuilderModal] API endpoint or response example not found:', arrayVar.sourceApiEndpoint);
      return [];
    }
    
    console.log('🌐 [ListBuilderModal] Found API endpoint with response example:', apiEndpoint.name);
    console.log('🌐 [ListBuilderModal] Response example:', apiEndpoint.responseExample);
    
    // Extract fields from response example
    let responseData = apiEndpoint.responseExample;
    
    // Handle different response structures
    let arrayData = null;
    if (Array.isArray(responseData)) {
      arrayData = responseData;
    } else if (responseData && responseData.data && Array.isArray(responseData.data)) {
      arrayData = responseData.data;
    } else if (responseData && typeof responseData === 'object') {
      // Look for arrays in the response object
      Object.keys(responseData).forEach(key => {
        if (Array.isArray(responseData[key]) && responseData[key].length > 0) {
          arrayData = responseData[key];
        }
      });
    }
    
    if (!arrayData || arrayData.length === 0) {
      console.log('🔍 [ListBuilderModal] No array data found in API endpoint response example');
      return [];
    }
    
    console.log('🌐 [ListBuilderModal] Extracting fields from API response array:', arrayData.length, 'items');
    
    const fields = extractFieldsFromData(arrayData);
    
    // Return fields with sample data attached
    return Object.assign(fields, { sampleData: arrayData });
  };

  const formatFieldLabel = (path) => {
    return path
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' → ');
  };

  const handlePresetChange = (presetKey) => {
    setSelectedPreset(presetKey);
    
    if (presetKey === 'numbered_list') {
      setFormatOptions({
        ...formatOptions,
        numbered: true,
        separatorType: 'line_break',
        fieldSeparator: '',
        fieldsOnSeparateLines: false,
        showFieldLabels: false
      });
    } else if (presetKey === 'detailed_card') {
      setFormatOptions({
        ...formatOptions,
        numbered: false,
        separatorType: 'separator',
        fieldSeparator: '\n',
        customSeparator: '---',
        fieldsOnSeparateLines: true,
        showFieldLabels: true
      });
    } else if (presetKey === 'simple_options') {
      setFormatOptions({
        ...formatOptions,
        numbered: true,
        separatorType: 'line_break',
        fieldSeparator: ' - ',
        fieldsOnSeparateLines: false,
        showFieldLabels: false
      });
    }
  };

  const handleFieldToggle = (field) => {
    const isSelected = selectedFields.some(f => f.path === field.path);
    
    if (isSelected) {
      setSelectedFields(selectedFields.filter(f => f.path !== field.path));
    } else {
      setSelectedFields([...selectedFields, field]);
    }
  };

  const handleUpdateField = (fieldPath, updatedField) => {
    setSelectedFields(selectedFields.map(field => 
      field.path === fieldPath ? updatedField : field
    ));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSelectedFields((items) => {
        const oldIndex = items.findIndex(item => item.path === active.id);
        const newIndex = items.findIndex(item => item.path === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const generateTemplate = () => {
    if (!selectedArray || selectedFields.length === 0) return '';
    
    let template = '';
    
    // Add numbering if enabled
    if (formatOptions.numbered) {
      template += '{#}. ';
    }
    
    // Handle fields on separate lines vs inline
    if (formatOptions.fieldsOnSeparateLines) {
      // Each field on its own line with optional labels
      const fieldLines = selectedFields.map(field => {
        let fieldTemplate = '';
        if (field.customLabel) {
          fieldTemplate = `${field.customLabel} {${field.path}}`;
        } else {
          fieldTemplate = `{${field.path}}`;
        }
        return fieldTemplate;
      });
      
      template += fieldLines.join('\\n');
    } else {
      // Fields inline with separator
      const fieldParts = selectedFields.map(field => `{${field.path}}`);
      template += fieldParts.join(formatOptions.fieldSeparator);
    }
    
    // Add line separator between items
    if (formatOptions.separatorType === 'separator' && formatOptions.customSeparator) {
      template += `\\n${formatOptions.customSeparator}`;
    } else {
      template += '\\n';
    }
    
    return `{{${selectedArray}[*]|${template}}}`;
  };

  const generatePreview = () => {
    if (!arrayStructure || !arrayStructure.sampleData || selectedFields.length === 0) {
      setPreviewText('Select fields to see preview...');
      return;
    }

    const sampleItems = arrayStructure.sampleData.slice(0, 2);
    const previews = sampleItems.map((item, index) => {
      let formatted = '';
      
      // Add numbering
      if (formatOptions.numbered) {
        formatted += `${index + 1}. `;
      }
      
      // Handle fields on separate lines vs inline
      if (formatOptions.fieldsOnSeparateLines) {
        // Each field on its own line with optional labels
        const fieldLines = selectedFields.map(field => {
          const value = getNestedValue(item, field.path) || '[missing]';
          if (field.customLabel) {
            return `${field.customLabel} ${value}`;
          } else {
            return value;
          }
        });
        
        formatted += fieldLines.join('\n');
      } else {
        // Fields inline with separator
        const fieldValues = selectedFields.map(field => {
          const value = getNestedValue(item, field.path);
          return value || '[missing]';
        });
        
        formatted += fieldValues.join(formatOptions.fieldSeparator);
      }
      
      return formatted;
    }).join('\n\n'); // Extra line break between items for readability
    
    // Add separator if configured
    if (formatOptions.separatorType === 'separator' && formatOptions.customSeparator) {
      const separator = formatOptions.customSeparator;
      setPreviewText(previews.replace(/\n\n/g, `\n${separator}\n\n`));
    } else {
      setPreviewText(previews);
    }
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  const handleInsert = () => {
    const template = generateTemplate();
    if (template) {
      onInsertTemplate(template);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '80vh', maxHeight: '800px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ListIcon color="primary" />
          <Typography variant="h6">Create List Format</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Left Panel: Configuration */}
          <Grid item xs={7} sx={{ p: 3, borderRight: '1px solid #e0e0e0' }}>
            {/* Array Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                1. Select Array Variable
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Array Variable</InputLabel>
                <Select
                  value={selectedArray}
                  onChange={(e) => setSelectedArray(e.target.value)}
                  label="Array Variable"
                >
                  {availableArrays.map((arrayVar) => (
                    <MenuItem key={arrayVar.name} value={arrayVar.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{arrayVar.name}</Typography>
                        <Chip 
                          label={`${arrayVar.sampleData?.length || 0} items`}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Template Presets */}
            {selectedArray && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  2. Choose Template Style
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(TEMPLATE_PRESETS).map(([key, preset]) => (
                    <Chip
                      key={key}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{preset.icon}</span>
                          <span>{preset.name}</span>
                        </Box>
                      }
                      variant={selectedPreset === key ? "filled" : "outlined"}
                      color={selectedPreset === key ? "primary" : "default"}
                      onClick={() => handlePresetChange(key)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Field Selection */}
            {arrayStructure && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    3. Select Fields to Display
                  </Typography>
                  {arrayStructure.source === 'api_endpoint' && (
                    <Box sx={{ 
                      backgroundColor: '#e3f2fd', 
                      px: 1.5, 
                      py: 0.5, 
                      borderRadius: 1,
                      border: '1px solid #bbdefb'
                    }}>
                      <Typography variant="caption" sx={{ 
                        color: '#1565c0', 
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}>
                        🌐 From API Response
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Paper sx={{ p: 2, maxHeight: 200, overflow: 'auto' }}>
                  {arrayStructure.fields.map((field) => (
                    <Box
                      key={field.path}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        mb: 1,
                        borderRadius: 1,
                        backgroundColor: selectedFields.some(f => f.path === field.path) ? '#e3f2fd' : '#f5f5f5',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#eeeeee' }
                      }}
                      onClick={() => handleFieldToggle(field)}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {field.label}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {field.path} • {field.sampleValue}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={selectedFields.some(f => f.path === field.path) ? "Selected" : "Add"}
                        color={selectedFields.some(f => f.path === field.path) ? "primary" : "default"}
                      />
                    </Box>
                  ))}
                </Paper>
              </Box>
            )}

            {/* Selected Fields Order */}
            {selectedFields.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  4. Arrange Field Order
                </Typography>
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={selectedFields.map(field => field.path)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Paper sx={{ p: 1 }}>
                      {selectedFields.map((field) => (
                        <SortableItem
                          key={field.path}
                          field={field}
                          onRemove={(path) => setSelectedFields(selectedFields.filter(f => f.path !== path))}
                          onUpdateField={handleUpdateField}
                          formatOptions={formatOptions}
                        />
                      ))}
                    </Paper>
                  </SortableContext>
                </DndContext>
              </Box>
            )}

            {/* Format Options */}
            {selectedFields.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  5. Format Options
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formatOptions.numbered}
                        onChange={(e) => setFormatOptions({...formatOptions, numbered: e.target.checked})}
                      />
                    }
                    label="Add numbering (1. 2. 3.)"
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formatOptions.fieldsOnSeparateLines}
                        onChange={(e) => setFormatOptions({...formatOptions, fieldsOnSeparateLines: e.target.checked})}
                      />
                    }
                    label="Put each field on a separate line"
                  />
                  
                  {!formatOptions.fieldsOnSeparateLines && (
                    <TextField
                      size="small"
                      label="Field Separator"
                      value={formatOptions.fieldSeparator}
                      onChange={(e) => setFormatOptions({...formatOptions, fieldSeparator: e.target.value})}
                      placeholder="e.g. ' - ' or ' | '"
                      helperText="Text between fields (when fields are inline)"
                    />
                  )}
                  
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Item Separator</FormLabel>
                    <RadioGroup
                      value={formatOptions.separatorType}
                      onChange={(e) => setFormatOptions({...formatOptions, separatorType: e.target.value})}
                    >
                      <FormControlLabel value="line_break" control={<Radio size="small" />} label="Line break only" />
                      <FormControlLabel value="separator" control={<Radio size="small" />} label="Custom separator" />
                    </RadioGroup>
                  </FormControl>
                  
                  {formatOptions.separatorType === 'separator' && (
                    <TextField
                      size="small"
                      label="Custom Separator"
                      value={formatOptions.customSeparator}
                      onChange={(e) => setFormatOptions({...formatOptions, customSeparator: e.target.value})}
                      placeholder="e.g. '---' or '• • •'"
                      helperText="Separator between list items"
                    />
                  )}
                </Box>
              </Box>
            )}
          </Grid>

          {/* Right Panel: Live Preview */}
          <Grid item xs={5} sx={{ p: 3, backgroundColor: '#fafafa' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              Live Preview
            </Typography>
            
            {selectedArray && selectedFields.length > 0 ? (
              <Box>
                <Paper sx={{ p: 2, mb: 2, minHeight: 200, backgroundColor: 'white' }}>
                  <Typography variant="caption" color="textSecondary" gutterBottom>
                    How your list will appear:
                  </Typography>
                  <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.9rem', whiteSpace: 'pre-line' }}>
                    {previewText}
                  </Box>
                </Paper>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    <strong>Generated Template:</strong><br />
                    <code style={{ fontSize: '0.8rem' }}>{generateTemplate()}</code>
                  </Typography>
                </Alert>
              </Box>
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: 'white' }}>
                <Typography variant="body2" color="textSecondary">
                  {!selectedArray 
                    ? 'Select an array variable to begin' 
                    : 'Choose fields to see preview'
                  }
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleInsert}
          variant="contained"
          disabled={!selectedArray || selectedFields.length === 0}
          startIcon={<AddIcon />}
        >
          Insert List Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ListBuilderModal; 